import { memo } from 'react';
import { Button } from '../../../components/Button';
import { useReferralSaleStateTranslations } from '../../../hooks/translations/useReferralRoleTranslations';
import { useFormatDate } from '../../../hooks/useFormatDate';
import { T } from '../../../translation/src';
import { CreditLedgerEntryType, FeePricingKind, ReferralDealKind, ReferralRole, ReferralSaleFieldsFragment } from '../../../types/graphqlTypes';
import { formatPrice } from '../../../utils/formatPrice';
import { cutPercent } from './partnerRoles';
import { usePartnerTerms } from './usePartnerTerms';

// What a sale sold, in a few words: the subscription and how it came about, or the projects bought.
const WhatWasSold: React.FC<{ sale: ReferralSaleFieldsFragment }> = ({ sale }) => {
  if (sale.ledgerEntryType === CreditLedgerEntryType.Grant) return <T _str="{count} extra projects" args={{ count: sale.credits ?? 0 }} />;
  const plan = sale.subscriptionType ?? '';
  switch (sale.pricingKind) {
    case FeePricingKind.NewSubscription:
      return <T _str="new {plan} subscription" args={{ plan }} />;
    case FeePricingKind.Upgrade:
      return <T _str="upgrade to {plan}" args={{ plan }} />;
    case FeePricingKind.Renewal:
      return <T _str="{plan} renewal" args={{ plan }} />;
    case FeePricingKind.ScheduledDowngrade:
      return <T _str="{plan} from the next term" args={{ plan }} />;
    default:
      return <T _str="{plan} subscription" args={{ plan }} />;
  }
};

interface SalesHistoryProps {
  sales: ReferralSaleFieldsFragment[];
  // The partner's role: its orders (a reseller, a full-service partner) or its sales (a lead generator).
  role: ReferralRole;
  // One customer's sales (its detail): the customer column is left out.
  hideCustomer?: boolean;
  // The sales page: every row, no scroll box of its own.
  fullHeight?: boolean;
  // A button in the header to the sales page, with its filters.
  onManage?: () => void;
}
// A partner's orders or sales, newest first: what was ordered or sold for whom, its price, and the partner's
// share (the opening subscription at the first rate, the rest at the recurring one). A reseller's or full-service
// partner's order shows its discount and what it pays, and the invoice it is on; a lead generator's sale its
// commission.
const SalesHistory_: React.FC<SalesHistoryProps> = ({ sales, role, hideCustomer, fullHeight, onManage }) => {
  const terms = usePartnerTerms(role);
  const states = useReferralSaleStateTranslations();
  const { formatDay } = useFormatDate();
  return (
    <div className="flex flex-col rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-[#242424]">
      <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
        <span className="text-base font-semibold">{terms.deals}</span>
        <div className="flex items-center gap-3">
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">{sales.length}</span>
          {onManage && (
            <Button variant="secondary" size="xs" onClick={onManage}>
              <span className="whitespace-nowrap">{terms.manageDeals}</span>
            </Button>
          )}
        </div>
      </div>
      {sales.length === 0 ? (
        <div className="px-6 py-10 text-center text-sm text-gray-500">
          {terms.orders ? <T _str="No orders yet. Every subscription, upgrade, renewal and project purchase shows up here." /> : <T _str="No sales yet. Every subscription, upgrade, renewal and project purchase shows up here." />}
        </div>
      ) : (
        <div className={fullHeight ? 'overflow-x-auto' : 'max-h-[28rem] overflow-auto'}>
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-white text-xs uppercase tracking-wide text-gray-500 dark:bg-[#242424]">
              <tr>
                <th className="px-6 py-2 font-medium">
                  <T _str="date" swc />
                </th>
                {!hideCustomer && (
                  <th className="px-2 py-2 font-medium">
                    <T _str="customer" swc />
                  </th>
                )}
                <th className="px-2 py-2 font-medium">{terms.dealt}</th>
                <th className="px-2 py-2 text-right font-medium">
                  <T _str="price" swc />
                </th>
                <th className="px-2 py-2 text-right font-medium">{terms.share}</th>
                {terms.orders && <th className="px-2 py-2 text-right font-medium">{terms.yourShare}</th>}
                <th className="px-6 py-2 font-medium">
                  <T _str="status" swc />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sales.map((sale) => (
                <tr key={sale.id}>
                  <td className="whitespace-nowrap px-6 py-2">{formatDay(sale.soldAt)}</td>
                  {!hideCustomer && <td className="max-w-[12rem] truncate px-2 py-2">{sale.referredOrganisationName}</td>}
                  <td className="px-2 py-2">
                    <WhatWasSold sale={sale} />
                    {sale.dealKind === ReferralDealKind.First && (
                      <span className="ml-2 inline-block whitespace-nowrap rounded-full bg-neon-green-500/20 px-2 py-0.5 text-xs text-dark-green-700 dark:text-neon-green-300">{terms.firstDeal}</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 text-right">{formatPrice(sale.amountCents)}</td>
                  <td className="whitespace-nowrap px-2 py-2 text-right">
                    {formatPrice(sale.cutCents)} <span className="text-xs text-gray-500">({cutPercent(sale.cutBasisPoints)}%)</span>
                  </td>
                  {terms.orders && <td className="whitespace-nowrap px-2 py-2 text-right font-semibold">{formatPrice(sale.amountCents - sale.cutCents)}</td>}
                  <td className="whitespace-nowrap px-6 py-2 text-xs text-gray-500">
                    {/* Ordered by the partner: its invoice, once sales has checked the order. */}
                    {sale.deductedOnInvoice ? sale.odooInvoiceId ? <T _str="on invoice #{id}" args={{ id: sale.odooInvoiceId }} /> : <T _str="invoice follows" /> : states[sale.state]}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
export const SalesHistory = memo(SalesHistory_);
